/**
 * Die paar Bausteine, die jede Seite dieser App braucht. Bewusst klein: bei
 * sieben Seiten kostet eine Komponentenbibliothek mehr, als sie spart.
 */

import { SprachWahl } from './sprache-wahl';
import { useEffect, useId, useState, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useKopfGuthaben } from './guthaben-kopf';
import { CLAUDE_NEU, ersterLaufVorschlag, type Aufgabe, type AufgabeId, type Vorschlag } from './erster-lauf';
import { EINRICHTUNG } from './preise';
import { oeffneCookieEinstellungen } from './messung';
import { STANDARDZIEL } from './ziel';
import { locale, t } from './sprache';

/** Die drei Schritte bis zum ersten Lauf. Wer registriert, soll sehen, dass nach dem Formular noch zwei kommen. */
const EINSTIEG_DE = [
  { titel: 'Konto anlegen', text: 'E-Mail und Passwort, keine Zahlungsdaten.' },
  { titel: 'Adresse bestätigen', text: 'Danach wird dein Startguthaben gebucht.' },
  { titel: 'In Claude verbinden', text: 'Ein Befehl in Claude Code oder ein Connector in claude.ai.' },
] as const;
const EINSTIEG_EN = [
  { titel: 'Create account', text: 'Email and password, no payment details.' },
  { titel: 'Confirm address', text: 'Then your starting credits are added.' },
  { titel: 'Connect in Claude', text: 'One command in Claude Code or a connector in claude.ai.' },
] as const;
/** Beim Rendern aufrufen: die Sprache wird beim Aufruf gelesen. */
const einstieg = () => t<ReadonlyArray<{ titel: string; text: string }>>(EINSTIEG_DE, EINSTIEG_EN);

/**
 * Was 99takes tut, in vier Schritten, auf der Registrierung (Feedback
 * Testnutzer, 15.10.2026: „ohne Vorwissen verstehe ich nicht, was ihr
 * eigentlich macht"; der Rundgang erklärt es, kommt aber erst nach Bestätigung
 * und Fragen). Dieselbe Geschichte wie die vier Szenen in Einfuehrung.tsx, nur
 * kürzer; wer dort etwas ändert, prüft, ob es hier noch stimmt.
 */
export const SO_GEHTS = [
  { titel: 'Du fragst in Claude.', text: 'Etwa, welche von zwei Headlines bei deinen Kunden besser ankommt. Oder du nennst die Adresse deiner Seite.' },
  { titel: '99takes baut deine Zielgruppe.', text: 'Simulierte Leser, sogenannte Personas, recherchiert aus echten Kundenstimmen zu deinem Angebot.' },
  { titel: 'Die Personas lesen mit.', text: 'Jede liest deinen Text und eine zweite Fassung, ohne zu wissen, welche neu ist.' },
  { titel: 'Du bekommst ein Urteil.', text: 'Welche Fassung gewinnt, der wichtigste Einwand und was du ändern solltest.' },
] as const;

/** Dieselben vier Schritte auf Englisch. Gewählt wird beim Rendern mit `t(SO_GEHTS, SO_GEHTS_EN)`. */
export const SO_GEHTS_EN: ReadonlyArray<{ titel: string; text: string }> = [
  { titel: 'You ask in Claude.', text: 'For example, which of two headlines lands better with your customers. Or you give the address of your page.' },
  { titel: '99takes builds your audience.', text: 'Simulated readers, called personas, researched from real customer voices about your offer.' },
  { titel: 'The personas read along.', text: 'Each one reads your text and a second version, without knowing which one is new.' },
  { titel: 'You get a verdict.', text: 'Which version wins, the main objection and what you should change.' },
];

/** Was nichts kostet, unter den vier Schritten. */
const KOSTENLOS = 'Konto anlegen, Adresse bestätigen und mit Claude verbinden kosten nichts. Für deine Zielgruppe und die Tests bekommst du ein Startguthaben.';
const KOSTENLOS_EN = 'Creating an account, confirming your address and connecting to Claude cost nothing. For your audience and the tests you get starting credits.';

type Zustand = 'erledigt' | 'jetzt' | 'offen';
const zustandVon = (n: number, schritt: number, fertig = false): Zustand =>
  (n < schritt || (fertig && n === schritt) ? 'erledigt' : n === schritt ? 'jetzt' : 'offen');

/** Ein Haken, wie er im Einstieg und unter dem Knopf steht. */
export function Haken({ className = 'size-3.5' }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className}`}>
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  );
}

/**
 * Rahmen der Seiten vor der Anmeldung: genau eine Handlung auf einer Karte.
 *
 * Auf den Seiten des Einstiegs (`schritt` gesetzt) steht auf breiten
 * Bildschirmen rechts neben der Karte eine Spalte: was 99takes tut und wo im Einstieg man
 * steht, mit einem Satz je Schritt. Auf dem Telefon steht oben eine Zeile mit
 * Wortmarke und „Schritt n von 3", darunter drei schmale Balken, und das
 * Formular steht ohne Karte auf dem Grund (Entwurf „99takes Signup Politur",
 * 12.10.2026: bei 340 x 640 lag „Konto anlegen" vorher unter dem Rand).
 * Anmelden, Passwort und die Zustimmungsseite bleiben zentriert: dort ist
 * der Einstieg vorbei oder es ist ein fremder Ablauf.
 */
export function Rahmen({ titel, unter, children, fuss, schritt, erledigt = false }: {
  titel: string;
  unter?: ReactNode;
  children: ReactNode;
  fuss?: ReactNode;
  /** 1 bis 3: zeigt, wo im Einstieg jemand steht. Nur auf den Seiten des Einstiegs. */
  schritt?: 1 | 2 | 3;
  /** Der Schritt ist schon geschafft (Adresse bestätigt): Haken statt Nummer. */
  erledigt?: boolean;
}) {
  useEffect(() => {
    document.title = `${titel} · 99takes`;
  }, [titel]);

  if (!schritt) {
    return (
      <main className="grid min-h-dvh place-items-center px-4 py-10">
        <div className="w-full max-w-[26rem]">
          <Link to="/" className="mx-auto mb-8 block w-fit rounded-md no-underline"><Wortmarke className="h-8" /></Link>
          <div className="-mt-4 mb-4 flex justify-end"><SprachWahl /></div>
          <div className="rounded-karte border border-linie bg-flaeche p-5 shadow-karte sm:p-7">
            <h1 className="m-0 mb-2 font-serif text-[1.75rem] leading-[1.15] font-medium tracking-[-0.025em] text-tinte">{titel}</h1>
            {unter && <div className="mt-0 mb-6 text-[0.9375rem] leading-relaxed text-text-leise">{unter}</div>}
            {children}
          </div>
          {fuss && <p className="mt-5 text-center text-sm leading-relaxed text-text-hell">{fuss}</p>}
        </div>
      </main>
    );
  }

  const stand = erledigt ? t(`${schritt} von 3 erledigt`, `${schritt} of 3 done`) : t(`Schritt ${schritt} von 3`, `Step ${schritt} of 3`);
  const EINSTIEG = einstieg();

  // Formular links, Erklaerung rechts (Feedback Testnutzer, 28.09.2026): die
  // Karte ist das, weswegen man hier ist, und steht deshalb dort, wo man zu
  // lesen anfaengt. Die Wortmarke steht darueber und gehoert keiner Spalte.
  // Auf dem Telefon faellt die Erklaerung weg, das Formular steht oben. Die
  // Registrierung hat ihren eigenen Rahmen (RegistrierRahmen).
  return (
    <main className="min-h-dvh px-4 pt-[1.125rem] pb-10 lg:grid lg:place-items-center lg:py-16">
      <div className="mx-auto w-full max-w-[26rem] lg:max-w-[60rem]">
        <div className="mb-[1.125rem] lg:mb-10">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="block w-fit rounded-md no-underline"><Wortmarke className="h-[1.375rem] lg:h-8" /></Link>
            <span className="flex items-center gap-3"><span className="text-[0.75rem] text-text-hell lg:hidden">{stand}</span><SprachWahl /></span>
          </div>
          <ol aria-label={t('Einstieg', 'Getting started')} className="m-0 mt-3 grid list-none grid-cols-3 gap-1.5 p-0 lg:hidden">
            {EINSTIEG.map((s, i) => {
              const zustand = zustandVon(i + 1, schritt, erledigt);
              return (
                <li key={s.titel} aria-current={zustand === 'jetzt' ? 'step' : undefined} className={`h-1 rounded-full ${zustand === 'offen' ? 'bg-linie' : 'bg-marke'}`}>
                  <span className="sr-only">{zustand === 'erledigt' ? t('Erledigt: ', 'Done: ') : ''}{s.titel}</span>
                </li>
              );
            })}
          </ol>
        </div>
        <div className="lg:grid lg:grid-cols-[26rem_minmax(0,1fr)] lg:items-center lg:gap-16">
          <div className="w-full max-w-[26rem]">
            <div className="lg:rounded-karte lg:border lg:border-linie lg:bg-flaeche lg:p-7 lg:shadow-karte">
              <div className="mb-1.5 hidden text-[0.75rem] font-semibold tracking-[0.07em] text-text-hell uppercase lg:block">{stand}</div>
              <h1 className="m-0 mb-1.5 font-serif text-[1.75rem] leading-[1.12] font-medium tracking-[-0.025em] text-tinte lg:text-[1.875rem]">{titel}</h1>
              {unter && <div className="mt-0 mb-5 text-[0.9375rem] leading-relaxed text-text-leise">{unter}</div>}
              {children}
            </div>
            {fuss && <p className="mt-5 text-center text-sm leading-relaxed text-text-hell">{fuss}</p>}
          </div>
          <div className="hidden lg:block">
            <p className="mt-0 mb-3 max-w-[20ch] font-serif text-[2.25rem] leading-[1.12] font-medium tracking-[-0.03em] text-tinte">
              {t('Texte gegen deine Zielgruppe prüfen, direkt in Claude.', 'Test your copy against your audience, right in Claude.')}
            </p>
            <p className="m-0 max-w-[40ch] text-[0.9375rem] leading-relaxed text-text-leise">
              {t(
                'Alle drei Schritte sind kostenlos. Credits brauchst du erst für deine Zielgruppe und die Runs, und dafür bekommst du ein Startguthaben.',
                'All three steps are free. You only need credits for your audience and the runs, and you get starting credits for that.',
              )}
            </p>
            <ol aria-label={t('Einstieg', 'Getting started')} className="m-0 mt-7 list-none p-0">
              {EINSTIEG.map((s, i) => {
                const zustand = zustandVon(i + 1, schritt, erledigt);
                return (
                  <li key={s.titel} className="grid grid-cols-[1.75rem_1fr] gap-3 py-2" aria-current={zustand === 'jetzt' ? 'step' : undefined}>
                    <span
                      aria-hidden
                      className={`grid size-7 place-items-center rounded-full border-[1.5px] text-[0.8125rem] font-bold ${
                        zustand === 'erledigt' ? 'border-knopf bg-knopf text-auf-marke'
                          : zustand === 'jetzt' ? 'border-marke bg-marke/[0.08] text-marke-tief'
                            : 'border-linie bg-flaeche text-text-hell'}`}
                    >
                      {zustand === 'erledigt' ? <Haken /> : i + 1}
                    </span>
                    <span>
                      <span className={`block text-[0.9375rem] font-semibold ${zustand === 'offen' ? 'text-text-leise' : 'text-tinte'}`}>
                        {zustand === 'erledigt' && <span className="sr-only">{t('Erledigt: ', 'Done: ')}</span>}{s.titel}
                      </span>
                      <span className="block text-[0.8125rem] leading-snug text-text-hell">{s.text}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * Rahmen der Registrierung, Schritt 1 des Einstiegs (Entwurf A „Ruhig, ein
 * Blatt“ im Canvas „99takes Registrierung Politur“, Claude Design,
 * https://claude.ai/artifact/HqBW7bVXbYwnCtbBVEMcQM, gewählt von Robert am
 * 15.10.2026). Anders als `Rahmen`: vor dem Konto zählt, was 99takes tut,
 * nicht wo im Einstieg man steht. Desktop: Karte links, oben bündig mit der
 * Erklärung rechts (Überschrift, ein Satz, die vier Schritte als Kacheln);
 * Schrittleiste in der Karte, „Schon ein Konto?“ oben rechts. Telefon: das
 * Formular zuerst, darunter die Erklärung, erreichbar über „Was ist 99takes?“.
 */
export function RegistrierRahmen({ titel, unter, children, anmelden }: {
  titel: string;
  unter: ReactNode;
  children: ReactNode;
  /** „Schon ein Konto? Anmelden“: am Desktop oben rechts, am Telefon unter dem Formular. */
  anmelden: ReactNode;
}) {
  useEffect(() => {
    document.title = `${titel} · 99takes`;
  }, [titel]);

  const balken = (
    <ol aria-label={t('Einstieg', 'Getting started')} className="m-0 grid list-none grid-cols-3 gap-1.5 p-0">
      {einstieg().map((s, i) => (
        <li key={s.titel} aria-current={i === 0 ? 'step' : undefined} className={`h-1 rounded-full ${i === 0 ? 'bg-marke' : 'bg-linie'}`}>
          <span className="sr-only">{s.titel}</span>
        </li>
      ))}
    </ol>
  );

  return (
    <main className="min-h-dvh px-4 pt-[1.125rem] pb-10 lg:px-12 lg:pt-14 lg:pb-16">
      <div className="mx-auto w-full max-w-[26rem] lg:max-w-[70rem]">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="block w-fit rounded-md no-underline"><Wortmarke className="h-[1.375rem] lg:h-[1.875rem]" /></Link>
          <span className="text-[0.75rem] text-text-hell lg:hidden">{t('Schritt 1 von 3', 'Step 1 of 3')}</span>
          <span className="flex items-center gap-4"><p className="m-0 hidden text-sm text-text-leise lg:block">{anmelden}</p><SprachWahl /></span>
        </div>
        <div className="mt-3 lg:hidden">{balken}</div>

        <div className="mt-[1.125rem] lg:mt-14 lg:grid lg:grid-cols-[27rem_minmax(0,1fr)] lg:items-start lg:gap-24">
          <div className="w-full max-w-[26rem] lg:max-w-none">
            <div className="lg:rounded-karte lg:border lg:border-linie lg:bg-flaeche lg:p-9 lg:shadow-karte">
              <div className="mb-[1.375rem] hidden lg:block">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-[0.75rem] font-semibold tracking-[0.08em] text-text-hell uppercase">{t('Schritt 1 von 3', 'Step 1 of 3')}</span>
                  <span aria-hidden className="text-[0.8125rem] text-text-hell">{t('Konto · Adresse · Claude', 'Account · Address · Claude')}</span>
                </div>
                {balken}
              </div>
              <h1 className="m-0 mb-1.5 font-serif text-[1.75rem] leading-[1.12] font-medium tracking-[-0.025em] text-tinte lg:mb-2 lg:text-[1.875rem]">{titel}</h1>
              <div className="mt-0 mb-5 text-[0.9375rem] leading-relaxed text-text-leise lg:mb-[1.375rem]">{unter}</div>
              {children}
            </div>
            <p className="mt-5 mb-0 text-sm text-text-leise lg:hidden">{anmelden}</p>
          </div>

          {/* Wer ohne Vorwissen kommt, soll vor dem Anlegen lesen können, was
              99takes tut (Feedback Testnutzer, 15.10.2026). Am Telefon unter
              dem Formular, damit der Knopf über dem Rand bleibt. */}
          <section id="was-ist-99takes" aria-labelledby="was-ist-99takes-titel" className="mt-8 scroll-mt-4 border-t border-linie-zart pt-6 lg:mt-0 lg:border-t-0 lg:pt-2">
            <h2 id="was-ist-99takes-titel" className="m-0 max-w-[16ch] font-serif text-[1.75rem] leading-[1.1] font-medium tracking-[-0.025em] text-tinte lg:text-[2.75rem] lg:leading-[1.06] lg:tracking-[-0.03em]">
              {t(
                <>Texte gegen deine Zielgruppe prüfen, <em className="font-normal text-marke-tief italic">direkt in Claude.</em></>,
                <>Test your copy against your audience, <em className="font-normal text-marke-tief italic">right in Claude.</em></>,
              )}
            </h2>
            <p className="mt-4 mb-0 max-w-[46ch] text-[0.9375rem] leading-relaxed text-text-leise lg:text-[1.0625rem]">
              {t(
                '99takes ist eine Erweiterung für Claude, den KI-Assistenten von Anthropic. Es hat kein eigenes Fenster, du arbeitest in deinem Chat.',
                '99takes is an extension for Claude, the AI assistant by Anthropic. It has no window of its own, you work in your chat.',
              )}
            </p>
            <ol aria-label={t('So funktioniert 99takes', 'How 99takes works')} className="m-0 mt-6 grid list-none gap-2.5 p-0 lg:mt-8 lg:grid-cols-2 lg:gap-3">
              {t<ReadonlyArray<{ titel: string; text: string }>>(SO_GEHTS, SO_GEHTS_EN).map((s, i) => (
                <li key={s.titel} className="flex flex-col gap-1.5 rounded-kasten bg-flaeche px-[1.125rem] py-4 lg:px-5 lg:py-[1.125rem]">
                  <span aria-hidden className="text-[0.75rem] font-semibold tracking-[0.08em] text-marke-tief">{i + 1}</span>
                  <b className="text-base font-semibold text-tinte">{s.titel}</b>
                  <span className="text-sm leading-normal text-text">{s.text}</span>
                </li>
              ))}
            </ol>
            <p className="mt-6 mb-0 max-w-[52ch] text-[0.8125rem] leading-relaxed text-text-hell lg:mt-8 lg:text-sm">{t(KOSTENLOS, KOSTENLOS_EN)}</p>
          </section>
        </div>
      </div>
    </main>
  );
}

export function Feld({ label, hinweis, ...rest }: { label: string; hinweis?: string } & InputHTMLAttributes<HTMLInputElement>) {
  // Passwort einblendbar: wer auf dem Telefon tippt, sieht sonst erst nach dem
  // Absenden, dass ein Buchstabe falsch war — und bei der Registrierung gar nicht.
  const [sichtbar, setSichtbar] = useState(false);
  const hinweisId = useId();
  const istPasswort = rest.type === 'password';
  return (
    <label className="block mb-4">
      <span className="mb-1.5 block text-[0.84375rem] font-medium text-text">{label}</span>
      <span className="relative block">
        <input
          {...rest}
          aria-describedby={hinweis ? hinweisId : rest['aria-describedby']}
          type={istPasswort && sichtbar ? 'text' : rest.type}
          className={`box-border h-[2.75rem] w-full rounded-feld border border-linie-feld bg-flaeche px-3 text-[0.9375rem] text-text
                     shadow-[inset_0_1px_1px_rgb(20_18_12/0.04)] outline-none transition-[border-color,box-shadow] duration-150
                     placeholder:text-text-hell hover:border-linie-stark
                     focus:border-marke focus:shadow-[0_0_0_3px_rgb(63_138_135/0.16)] focus-visible:outline-none
                     disabled:bg-grund disabled:text-text-hell ${istPasswort ? 'pr-24' : ''}`}
        />
        {istPasswort && (
          <button
            type="button"
            onClick={() => setSichtbar(v => !v)}
            aria-pressed={sichtbar}
            className="absolute inset-y-1.5 right-1.5 cursor-pointer rounded-md border-0 bg-transparent px-2.5 text-[0.8125rem] text-text-leise transition-colors duration-150 hover:bg-grund hover:text-tinte"
          >
            {sichtbar ? t('Verbergen', 'Hide') : t('Anzeigen', 'Show')}
          </button>
        )}
      </span>
      {hinweis && <span id={hinweisId} aria-live="polite" className="block mt-1.5 text-[0.8125rem] text-text-hell">{hinweis}</span>}
    </label>
  );
}

/** Dreht sich nur, wenn der Nutzer Bewegung nicht abgeschaltet hat. */
export function Kreisel({ className = '' }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={`size-4 motion-safe:animate-spin ${className}`}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity=".25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Grauer Platzhalter in der Form dessen, was gleich kommt. Besser als „Wird
 * geladen …": die Seite springt nicht, wenn die Daten da sind.
 */
export function Platzhalter({ zeilen = 3 }: { zeilen?: number }) {
  return (
    <div aria-hidden className="space-y-3">
      {Array.from({ length: zeilen }, (_, i) => (
        <div key={i} className="h-4 rounded bg-linie-zart motion-safe:animate-pulse" style={{ width: `${[92, 70, 84, 60][i % 4]}%` }} />
      ))}
    </div>
  );
}

export function Knopf({ children, laedt, ...rest }: { laedt?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || laedt}
      className="h-12 w-full cursor-pointer rounded-full border-0 bg-knopf px-4 text-[0.9375rem] font-semibold text-auf-marke
                 transition-[background-color,transform] duration-150 hover:bg-knopf-hover active:translate-y-px
                 disabled:cursor-not-allowed disabled:opacity-60 disabled:active:translate-y-0"
    >
      {laedt
        ? <span className="inline-flex items-center justify-center gap-2"><Kreisel />{t('Einen Moment …', 'One moment …')}</span>
        : children}
    </button>
  );
}

/** `art` steuert nur die Farbe. Der Text traegt die Aussage. */
export function Hinweis({ art = 'info', children }: { art?: 'info' | 'gut' | 'schlecht'; children: ReactNode }) {
  const farbe = art === 'gut'
    ? 'text-gut border-gut/25 bg-gut/[0.06]'
    : art === 'schlecht'
      ? 'text-schlecht border-schlecht/25 bg-schlecht/[0.06]'
      : 'text-text-leise border-linie bg-ebene';
  return (
    <p role={art === 'schlecht' ? 'alert' : 'status'} className={`mb-4 px-3.5 py-3 text-sm leading-relaxed rounded-kasten border ${farbe}`}>
      {children}
    </p>
  );
}

export function Verweis({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-marke-tief underline decoration-marke/35 underline-offset-[3px] transition-colors duration-150 hover:decoration-marke">
      {children}
    </Link>
  );
}

/**
 * Das Zeichen aus dem Logo: sechs Splitter um einen Würfel (seit 12.10.2026,
 * Entwurf „99takes Marke Politur“). Nachgebaut aus der Vorlage der Website
 * (99takes-website/public/logo.svg, ein PNG): dieselbe Form, Drehrichtung und
 * Farbfolge, aber als Vektor mit gleich breiten Fugen und flachen Farben.
 * Die Vorlage hatte verrauschte Flächen und einen Rest Text im Teal-Splitter.
 *
 * Geometrie: spitzes Sechseck mit Radius 48 um (50, 50), innen ein Sechseck
 * mit Radius 21. Jede Innenseite, über ihre Ecke hinaus verlängert, schneidet
 * den Ring; so entstehen die sechs Splitter. Die Fugen sind eine Maske, also
 * wirklich durchsichtig, und das Zeichen steht auf jedem Grund.
 *
 * Die Farben stehen als Klassen in index.css (`.z-…`), mit eigener Fassung
 * für den Dunkelmodus und `.immer-hell`.
 */
const SPLITTER = [
  ['z-l', 'M31.81 60.5L31.81 12.5L8.43 26L8.43 47Z'],
  ['z-o', 'M31.81 39.5L73.38 15.5L50 2L31.81 12.5Z'],
  ['z-or', 'M50 29L91.57 53L91.57 26L73.38 15.5Z'],
  ['z-r', 'M68.19 39.5L68.19 87.5L91.57 74L91.57 53Z'],
  ['z-u', 'M68.19 60.5L26.62 84.5L50 98L68.19 87.5Z'],
  ['z-ul', 'M50 71L8.43 47L8.43 74L26.62 84.5Z'],
  ['z-wo', 'M50 29L68.19 39.5L50 50L31.81 39.5Z'],
  ['z-wr', 'M68.19 39.5L68.19 60.5L50 71L50 50Z'],
  ['z-wl', 'M31.81 39.5L50 50L50 71L31.81 60.5Z'],
] as const;
const SCHNITTE = 'M50 29L91.57 53M68.19 39.5L68.19 87.5M68.19 60.5L26.62 84.5M50 71L8.43 47M31.81 60.5L31.81 12.5M31.81 39.5L73.38 15.5';
/** Breite des Schriftzugs im viewBox, gemessen an Hanken Grotesk (siehe Wortmarke). */
const WORT_BREITE = 226;
const INNEN = 'M50 29L68.19 39.5L68.19 60.5L50 71L31.81 60.5L31.81 39.5Z';

/**
 * Nur das Zeichen. `klein` unter 32 px: breitere Fugen, sonst läuft es zu
 * einem Fleck zu. `rahmen` setzt es als verschachteltes SVG an eine Stelle
 * eines umgebenden SVG (Wortmarke).
 */
export function Zeichen({ className = 'size-6', klein = false, titel, rahmen }: {
  className?: string;
  klein?: boolean;
  titel?: string;
  rahmen?: { x: number; y: number; groesse: number };
}) {
  const id = `z${useId().replace(/[^A-Za-z0-9]/g, '')}`;
  const fuge = klein ? 6 : 3.4;
  const lage = rahmen ? { x: rahmen.x, y: rahmen.y, width: rahmen.groesse, height: rahmen.groesse } : {};
  return (
    <svg
      viewBox="0 0 100 100" {...lage}
      className={rahmen ? 'zeichen' : `zeichen shrink-0 ${className}`}
      role={titel ? 'img' : undefined} aria-label={titel} aria-hidden={titel ? undefined : true}
    >
      <defs>
        <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100">
          <rect width="100" height="100" fill="#fff" />
          <path d={SCHNITTE} stroke="#000" strokeWidth={fuge} />
          <path d={INNEN} fill="none" stroke="#000" strokeWidth={fuge} strokeLinejoin="round" />
        </mask>
      </defs>
      <g mask={`url(#${id})`}>
        {SPLITTER.map(([k, d]) => <path key={k} className={k} d={d} />)}
      </g>
    </svg>
  );
}

/**
 * Zeichen und Schriftzug in einem SVG, damit beides gemeinsam mit der Höhe
 * skaliert (`h-7` und so weiter). Abstand 18 Einheiten wie in der Vorlage, die
 * 99 fetter als „takes“. Der Schriftzug ist Text in Hanken Grotesk, nicht in
 * Pfade gewandelt: die Schrift ist in der App ohnehin geladen. WORT_BREITE ist
 * an Hanken Grotesk gemessen (224,7 Einheiten); mit Ersatzschrift läuft der
 * Text etwas anders lang, abgeschnitten wird er nicht.
 */
export function Wortmarke({ className = 'h-7' }: { className?: string }) {
  return (
    <svg viewBox={`0 0 ${118 + WORT_BREITE} 100`} className={`block w-auto overflow-visible ${className}`} role="img" aria-label="99takes">
      <Zeichen rahmen={{ x: 0, y: 0, groesse: 100 }} />
      <text x="118" y="74" fontSize="66" letterSpacing="-1.6" className="fill-tinte" fontFamily="'Hanken Grotesk', system-ui, sans-serif" aria-hidden>
        <tspan fontWeight="700">99</tspan><tspan fontWeight="500">takes</tspan>
      </text>
    </svg>
  );
}

/**
 * Rahmen fuer die angemeldeten Seiten. Anderer Aufbau als `Rahmen`: dort geht
 * es um genau eine Handlung auf einer Karte, hier um Bleiben und Wechseln.
 *
 * Auf schmalen Geraeten zwei Zeilen: Marke und Abmelden oben, die Reiter
 * darunter. Die Reiter teilen sich dort die volle Breite, statt seitlich zu
 * scrollen: bei 340 px (Roberts Testtelefon) passten die fuenf nicht mehr in
 * den sichtbaren Bereich, und „Anleitung" verschwand still hinter dem Rand.
 *
 * `breite`: Tabellen und Listen bekommen mehr Platz als Fliesstext. Die
 * Anleitung bleibt schmal, weil lange Zeilen dort das Lesen bremsen.
 */
export function AppRahmen({ email, abmelden, children, breite = 'daten' }: {
  email: string;
  abmelden: () => void;
  children: ReactNode;
  breite?: 'daten' | 'text';
}) {
  const max = breite === 'text' ? 'max-w-[48rem]' : 'max-w-[60rem]';
  // Nur Konten in SIM_ADMIN_KONTEN; die Zahl kommt aus demselben Abruf wie das Guthaben.
  const admin = useKopfGuthaben()?.admin === true;
  return (
    <div className="min-h-dvh">
      <a href="#inhalt" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-10 focus:rounded-md focus:bg-flaeche focus:px-3 focus:py-2 focus:text-sm focus:text-tinte focus:shadow">{t('Zum Inhalt', 'Skip to content')}</a>
      <header className="border-b border-linie bg-flaeche">
        <div className={`mx-auto flex ${max} flex-wrap items-center px-4 sm:h-[3.75rem] sm:flex-nowrap sm:gap-8 sm:px-6`}>
          <Link to={STANDARDZIEL} className="-ml-1 rounded-md px-1 py-3.5 no-underline sm:py-0"><Wortmarke className="h-6 sm:h-7" /></Link>
          <nav
            aria-label={t('Bereiche', 'Sections')}
            className="order-3 -mx-4 flex w-[calc(100%+2rem)] justify-between border-t border-linie-zart px-3 sm:order-none sm:mx-0 sm:h-full sm:w-auto sm:justify-start sm:gap-1 sm:border-0 sm:px-0"
          >
            {/* Reihenfolge nach Gebrauch (03.10.2026): Runs ist die Startseite
                und der Grund wiederzukommen, Zielgruppen das Material dafuer,
                die Anleitung das Nachschlagen, das Konto das Seltene. Die
                Schluessel haengen am Konto: die meisten brauchen keinen, und
                fuenf gleichrangige Reiter sagten das Gegenteil. */}
            <Reiter to="/runs">Runs</Reiter>
            <Reiter to="/personas">{t('Zielgruppen', 'Audiences')}</Reiter>
            <Reiter to="/guide">{t('Anleitung', 'Guide')}</Reiter>
            <Reiter to="/account" auch={['/keys']}>{t('Konto', 'Account')}</Reiter>
            {admin && <Reiter to="/admin">Admin</Reiter>}
          </nav>
          <div className="ml-auto flex min-h-[3.25rem] items-center gap-2 sm:min-h-0">
            <GuthabenKopf />
            {/* Die Adresse steht nicht hier: sie passte nicht mehr in die
                Kopfzeile. Sie steht auf dem Konto und im Tooltip. */}
            <button
              type="button" onClick={abmelden} title={email ? t(`Abmelden (${email})`, `Sign out (${email})`) : undefined}
              className="hidden cursor-pointer rounded-full border border-linie bg-flaeche px-3 py-1.5 text-[0.8125rem] text-text-leise shadow-knopf transition-colors duration-150 hover:border-linie-stark hover:text-tinte sm:inline-flex"
            >
              {t('Abmelden', 'Sign out')}
            </button>
          </div>
        </div>
      </header>
      <main id="inhalt" tabIndex={-1} className={`einblenden mx-auto ${max} px-4 pt-7 pb-16 outline-none sm:px-6 sm:pt-10`}>{children}</main>
      <footer className={`mx-auto flex ${max} flex-wrap items-center gap-x-5 gap-y-2 px-4 pb-8 sm:px-6`}>
        <SprachWahl />
        {/* § 312k BGB: auf jeder Seite der App erreichbar (#271). */}
        <Link to="/kuendigen" className="text-[0.8125rem] text-text-hell underline decoration-linie underline-offset-[3px] hover:text-tinte">
          {t('Verträge hier kündigen', 'Cancel contracts here')}
        </Link>
        <button
          type="button" onClick={oeffneCookieEinstellungen}
          className="cursor-pointer border-0 bg-transparent p-0 text-[0.8125rem] text-text-hell underline decoration-linie underline-offset-[3px] hover:text-tinte"
        >
          {t('Cookie-Einstellungen', 'Cookie settings')}
        </button>
      </footer>
    </div>
  );
}

/** Der aktive Reiter bekommt eine Unterstreichung, nicht nur eine Farbe. `auch`: weitere Pfade, unter denen er aktiv ist. */
function Reiter({ to, auch = [], children }: { to: string; auch?: string[]; children: ReactNode }) {
  const { pathname } = useLocation();
  const mit = auch.some(p => pathname === p || pathname.startsWith(`${p}/`));
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `-mb-px flex shrink-0 items-center border-b-2 px-1 pt-2.5 pb-2 text-[0.8125rem] no-underline transition-colors duration-150 sm:h-full sm:px-2.5 sm:py-0 sm:text-sm ${
          isActive || mit ? 'border-marke font-semibold text-tinte' : 'border-transparent text-text-leise hover:border-linie hover:text-tinte'}`}
    >
      {children}
    </NavLink>
  );
}

/**
 * Das Guthaben als ruhige Marke neben „Abmelden", verlinkt aufs Konto. Ohne
 * Zahl (noch nicht geladen, Fehler) steht nichts da, statt einer Null, die
 * nach leerem Konto aussaehe.
 */
function GuthabenKopf() {
  const g = useKopfGuthaben();
  const { pathname } = useLocation();
  if (!g || pathname === '/account') return null;
  const knapp = g.credits != null && g.credits < 15;
  return (
    <Link
      to="/account"
      title={knapp ? t('Wenig Guthaben: unter Konto nachkaufen', 'Low balance: buy more under Account') : t('Verfügbares Guthaben', 'Available balance')}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1.5 text-[0.8125rem] whitespace-nowrap no-underline tabular-nums transition-colors duration-150 ${
        knapp ? 'border-marke/40 bg-marke/[0.08] text-marke-tief hover:border-marke' : 'border-transparent text-text-hell hover:text-tinte'}`}
    >
      {knapp && <span aria-hidden className="size-1.5 rounded-full bg-marke" />}
      {g.credits == null ? t('unbegrenzt', 'unlimited') : <><span className={`font-semibold ${knapp ? 'text-marke-tief' : 'text-text'}`}>{new Intl.NumberFormat(locale()).format(g.credits)}</span> {t('Credits', g.credits === 1 ? 'credit' : 'credits')}</>}
    </Link>
  );
}

/**
 * Kopf einer angemeldeten Seite: worum es hier geht, und die eine Aktion der
 * Seite rechts daneben. Jede Seite hat genau eine h1 — vorher gab es nur
 * Abschnittsueberschriften, und ein Screenreader fand keinen Seitentitel.
 */
export function Seitenkopf({ titel, unter, aktion, leise = false }: {
  titel: string; unter?: ReactNode; aktion?: ReactNode;
  /** Kleinerer Titel, wenn darunter etwas Wichtigeres steht (Run im Detail: das Urteil). */
  leise?: boolean;
}) {
  useEffect(() => {
    document.title = `${titel} · 99takes`;
  }, [titel]);
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 sm:mb-10">
      <div className="min-w-0 flex-1 basis-64">
        <h1 className={`m-0 font-serif leading-[1.15] tracking-[-0.015em] text-tinte ${leise
          ? 'text-[1.375rem] font-medium [text-wrap:balance] sm:text-[1.75rem]'
          : 'text-[1.875rem] leading-[1.1] font-medium tracking-[-0.025em] sm:text-[2.25rem]'}`}>{titel}</h1>
        {unter && <p className="mt-2 mb-0 max-w-[62ch] text-[0.9375rem] leading-relaxed text-text-leise">{unter}</p>}
      </div>
      {aktion}
    </div>
  );
}

/**
 * Eine Zeile, die jemand kopieren soll — Adresse, Befehl. Mit Knopf, weil
 * das Markieren einer umbrechenden Monospace-Zeile auf dem Handy fast nie
 * sauber gelingt, und ein halb kopierter Befehl scheitert still.
 */
// Code bricht an Leerzeichen um und mitten im Wort nur, wenn ein Teil allein
// zu lang ist (overflow-wrap: anywhere). break-all trennte „x-api-key" in
// „x-a" und „pi-key".
export function Kopierzeile({ text, anzeige, art = 'code' }: {
  text: string;
  anzeige?: ReactNode;
  /** 'text' fuer Saetze, die jemand in Claude eintippt — die lesen sich in Monospace wie Code. */
  art?: 'code' | 'text';
}) {
  const [kopiert, setKopiert] = useState(false);
  useEffect(() => {
    if (!kopiert) return;
    const t = setTimeout(() => setKopiert(false), 2000);
    return () => clearTimeout(t);
  }, [kopiert]);
  return (
    <div className="flex flex-col overflow-hidden rounded-kasten border border-linie bg-ebene min-[26rem]:flex-row min-[26rem]:items-stretch">
      {art === 'code' ? (
        <code className="min-w-0 flex-1 px-3.5 py-3 font-mono text-[0.8125rem] leading-relaxed whitespace-pre-wrap text-text [overflow-wrap:anywhere]">
          {anzeige ?? text}
        </code>
      ) : (
        <p className="m-0 min-w-0 flex-1 px-3.5 py-2.5 text-[0.9375rem] leading-relaxed whitespace-pre-wrap text-text">
          {anzeige ?? text}
        </p>
      )}
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(text).then(
            () => setKopiert(true),
            // Ohne Zwischenablage-Recht bleibt der Text markierbar stehen.
            e => console.warn('[kopieren]', e),
          );
        }}
        className="shrink-0 cursor-pointer border-0 border-t border-linie bg-flaeche px-3.5 py-2 text-[0.8125rem] font-medium text-text-leise transition-colors duration-150 hover:bg-grund hover:text-tinte focus-visible:outline-offset-[-2px] min-[26rem]:border-t-0 min-[26rem]:border-l min-[26rem]:py-0"
        aria-label={t('In die Zwischenablage kopieren', 'Copy to clipboard')}
      >
        <span aria-live="polite">{kopiert ? t('Kopiert', 'Copied') : t('Kopieren', 'Copy')}</span>
      </button>
    </div>
  );
}

/** Kleines Etikett. `art` steuert nur die Farbe, der Text traegt die Aussage. */
export function Marke({ art = 'neutral', children }: { art?: 'neutral' | 'gut' | 'schlecht' | 'marke'; children: ReactNode }) {
  const farbe = {
    neutral: 'bg-ebene text-text-leise border-linie',
    gut: 'bg-gut/[0.07] text-gut border-gut/20',
    schlecht: 'bg-schlecht/[0.06] text-schlecht border-schlecht/20',
    marke: 'bg-marke/[0.08] text-marke-tief border-marke/25',
  }[art];
  // Der Punkt traegt die Farbe, der Text bleibt lesbar. Neutral ohne Punkt:
  // dort gibt es keine Richtung, die er anzeigen koennte.
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.75rem] font-semibold whitespace-nowrap ${farbe}`}>
      {art !== 'neutral' && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/**
 * Ein Anteil als Balken. Die Beschriftung steht immer daneben: ein Balken
 * allein sagt „ungefaehr die Haelfte", und hier geht es um Credits und Belege.
 */
export function Balken({ wert, von, label }: { wert: number; von: number; label: string }) {
  const anteil = von > 0 ? Math.min(1, Math.max(0, wert / von)) : 0;
  return (
    <div
      role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={von} aria-valuenow={wert}
      className="h-1.5 w-full overflow-hidden rounded-full bg-linie-zart"
    >
      <div className="h-full rounded-full bg-marke" style={{ width: `${anteil * 100}%` }} />
    </div>
  );
}

/**
 * Ein Abschnitt einer angemeldeten Seite. Die Ueberschrift steht frei auf der
 * Seite; nur was ein Objekt ist (ein Lauf, ein Set, ein Schluessel), liegt in
 * einem `Panel`. Vorher steckte jeder Abschnitt in einer eigenen Karte, und
 * eine Seite war ein Stapel gleich aussehender Kaesten ohne Rangfolge.
 */
export function Abschnitt({ titel, zahl, unter, aktion, children, id }: {
  titel: string;
  /** Anzahl hinter der Ueberschrift, leiser gesetzt. */
  zahl?: number;
  unter?: ReactNode;
  aktion?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="mt-12 scroll-mt-4 first:mt-0">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-2">
        <h2 className="m-0 text-[0.75rem] font-semibold tracking-[0.07em] text-text-leise uppercase">
          {titel}
          {zahl != null && zahl > 0 && <span className="ml-2 font-medium text-text-hell tabular-nums">{zahl}</span>}
        </h2>
        {aktion}
      </div>
      {unter && <div className="mt-0 mb-3.5 max-w-[68ch] text-[0.875rem] leading-relaxed text-text-leise">{unter}</div>}
      {children}
    </section>
  );
}

/** Weisse Flaeche fuer Objekte. Zeilen darin trennt `Zeile`. */
export function Panel({ children, className = '', ...rest }: { children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={`rounded-karte border border-linie bg-flaeche shadow-flaeche ${className}`}>{children}</div>;
}

/** Eine Zeile in einem Panel. Gleiche Innenabstaende auf allen Seiten der App. */
export function Zeile({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:px-5 ${className}`}>{children}</div>;
}

/** Eine Zahl mit Beschriftung. Die Zahl traegt, die Beschriftung erklaert. */
export function Kennzahl({ label, wert, zusatz, gross = false }: { label: string; wert: ReactNode; zusatz?: ReactNode; gross?: boolean }) {
  return (
    <div>
      <div className="text-[0.8125rem] font-medium text-text-leise">{label}</div>
      <div className={`mt-1 leading-tight text-tinte tabular-nums ${gross ? 'font-serif text-[2.5rem] font-medium tracking-[-0.03em]' : 'text-[1.375rem] font-bold tracking-[-0.02em]'}`}>{wert}</div>
      {zusatz && <div className="mt-2 text-[0.8125rem] leading-relaxed text-text-leise">{zusatz}</div>}
    </div>
  );
}

/** Kleiner Knopf fuer Aktionen neben einer Ueberschrift oder in einer Zeile. */
export function KleinerKnopf({ children, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  // className wird angehaengt, nicht ersetzt: sonst verschluckt die Komponente
  // ein `ml-auto` vom Aufrufer, und der Knopf rutscht still an die falsche Stelle.
  return (
    <button
      {...rest}
      className={`inline-flex min-h-8 cursor-pointer items-center justify-center rounded-full border border-linie bg-flaeche px-3 py-1.5
                 text-center text-[0.8125rem] font-medium text-text shadow-knopf transition-colors duration-150
                 hover:border-linie-stark hover:bg-ebene hover:text-tinte active:bg-grund disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    >
      {children}
    </button>
  );
}

/** Die Tönung je Aufgabe, in der Reihenfolge des Design-Systems (docs/design-system). */
const TON: Record<AufgabeId, string> = { seite: 'bg-ton-petrol', headlines: 'bg-ton-blau', wettbewerb: 'bg-ton-pflaume' };

/**
 * Eine Aufgabe für den ersten Run: Dachzeile, der Satz für Claude, was
 * zurückkommt. Der Knopf kopiert den Satz und öffnet einen neuen Chat in
 * claude.ai, beides im selben Klick (sonst blockt der Browser das Fenster).
 * Der Satz steht nicht im Link: mit `?q=` zeigt claude.ai vor dem Prompt einen
 * Warnhinweis, den Nutzer nicht verstehen (siehe CLAUDE_NEU).
 */
function AufgabeKarte({ a }: { a: Aufgabe }) {
  const [kopiert, setKopiert] = useState(false);
  useEffect(() => {
    if (!kopiert) return;
    const t = setTimeout(() => setKopiert(false), 4000);
    return () => clearTimeout(t);
  }, [kopiert]);
  return (
    <li className={`flex flex-col rounded-[1rem] p-4 ${TON[a.id]}`}>
      <div className="text-[0.71875rem] font-semibold tracking-[0.06em] text-marke-tief uppercase">{a.dach}</div>
      <p className="m-0 mt-1.5 text-[0.96875rem] leading-snug font-semibold text-tinte">„{a.satz}“</p>
      <p className="m-0 mt-2 mb-3.5 text-[0.8125rem] leading-relaxed text-text-leise">{a.zurueck}</p>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(a.satz).then(
            () => setKopiert(true),
            // Ohne Zwischenablage-Recht steht der Satz weiter lesbar auf der Karte.
            e => console.warn('[kopieren]', e),
          );
          window.open(CLAUDE_NEU, '_blank', 'noopener');
        }}
        className="mt-auto inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full border border-linie bg-flaeche px-4 text-center text-[0.8125rem] font-semibold text-tinte shadow-knopf transition-colors duration-150 hover:border-linie-stark hover:bg-ebene"
      >
        <span aria-live="polite">{kopiert ? t('Kopiert. In Claude einfügen.', 'Copied. Paste it into Claude.') : t('Satz kopieren, Claude öffnen', 'Copy sentence, open Claude')}</span>
      </button>
    </li>
  );
}

/**
 * Der geführte erste Schritt für Konten ohne Run. Steht dort, wo sonst ein
 * leerer Zustand stünde (Runs, Konto), und sagt, was als Nächstes zu tun ist,
 * statt nur, dass nichts da ist.
 *
 * Seit 14.10.2026 (Entwurf C „Satz und Ergebnis“, erster-lauf.ts): erst
 * verbinden, dann drei Aufgaben zur Wahl, je ein Satz zum Kopieren. Vorher
 * standen dort „Zielgruppe aufbauen“ und „Seite als A/B-Test“ mit Lückentext;
 * die Zielgruppe baut Claude jetzt selbst auf, sobald eine Aufgabe sie braucht.
 *
 * `erledigt` nur aus Daten, die die Seite ohnehin hat. Was sie nicht weiß,
 * bleibt offen: ein Haken, der nicht stimmt, ist schlimmer als keiner.
 */
export function ErsterLauf({ erledigt = [], vorschlag = ersterLaufVorschlag(null), ohneVerbinden = false, passend = false, verbinden }: {
  erledigt?: Array<'verbunden'>;
  /** Auf dem Konto: die beiden Wege zum Verbinden direkt in Schritt 1 statt des Links dorthin. */
  verbinden?: ReactNode;
  /** Aufgaben passend zu den Onboarding-Antworten, siehe erster-lauf.ts. */
  vorschlag?: Vorschlag;
  /** Ohne den Schritt „Mit Claude verbinden“. */
  ohneVerbinden?: boolean;
  /** true, wenn die Reihenfolge aus echten Antworten kommt: dann steht das dabei. */
  passend?: boolean;
}) {
  const verbunden = erledigt.includes('verbunden');
  const runJetzt = ohneVerbinden || verbunden;
  const kreis = (fertig: boolean, jetzt: boolean) =>
    `grid size-7 place-items-center rounded-full border-[1.5px] text-[0.8125rem] font-bold ${
      fertig ? 'border-knopf bg-knopf text-auf-marke' : jetzt ? 'border-marke/40 bg-marke/[0.08] text-marke-tief' : 'border-linie text-text-hell'}`;
  return (
    <Panel>
      <ol className="m-0 list-none p-0">
        {!ohneVerbinden && (
          <li className="grid grid-cols-[1.75rem_1fr] gap-3.5 border-t border-linie-zart px-4 py-4 first:border-t-0 sm:px-5" aria-current={runJetzt ? undefined : 'step'}>
            <span aria-hidden className={kreis(verbunden, !verbunden)}>{verbunden ? '✓' : 1}</span>
            <div className="min-w-0">
              <div className={`text-[0.9375rem] font-semibold ${verbunden ? 'text-text-leise' : 'text-tinte'}`}>
                {verbunden && <span className="sr-only">{t('Erledigt: ', 'Done: ')}</span>}{t('Mit Claude verbinden', 'Connect to Claude')}
              </div>
              {!verbunden && (
                <>
                  <div className="mt-0.5 text-[0.8125rem] leading-relaxed text-text-hell">
                    {verbinden
                      ? t(
                        <>Einmal einrichten, danach fragst du in Claude ganz normal.{verbinden}<span className="mt-4 block">Zum Test danach in Claude:</span></>,
                        <>Set it up once, then you just ask in Claude as usual.{verbinden}<span className="mt-4 block">To test it, then in Claude:</span></>,
                      )
                      : t(
                        <>Einmal einrichten, danach fragst du in Claude ganz normal. <Verweis to="/account#verbinden">So geht es</Verweis>. Zum Test danach in Claude:</>,
                        <>Set it up once, then you just ask in Claude as usual. <Verweis to="/account#verbinden">How it works</Verweis>. To test it, then in Claude:</>,
                      )}
                  </div>
                  {/* Der Test-Satz belegt die Verbindung: get_balance antwortet mit dem Guthaben dieses Kontos. */}
                  <div className="mt-2.5"><Kopierzeile art="text" text={t('Bist du mit 99takes verbunden? Wie viel Guthaben habe ich?', 'Are you connected to 99takes? What is my balance?')} /></div>
                </>
              )}
            </div>
          </li>
        )}
        <li className="border-t border-linie-zart px-4 py-4 first:border-t-0 sm:px-5" aria-current={runJetzt ? 'step' : undefined}>
          <div className="grid grid-cols-[1.75rem_1fr] gap-3.5">
            <span aria-hidden className={kreis(false, runJetzt)}>{ohneVerbinden ? 1 : 2}</span>
            <div className="min-w-0">
              <div className="text-[0.9375rem] font-semibold text-tinte">{t('Dein erster Run. Such dir einen aus.', 'Your first run. Pick one.')}</div>
              <div className="mt-0.5 text-[0.8125rem] leading-relaxed text-text-hell">
                {t(
                  <>Satz kopieren, in Claude einfügen. Fehlt noch eine Zielgruppe, baut Claude sie zuerst mit dir auf
                ({EINRICHTUNG} Credits beim Speichern). Jeden Preis nennt Claude vorher.</>,
                  <>Copy the sentence, paste it into Claude. If you have no audience yet, Claude builds it with you first
                ({EINRICHTUNG} credits when saved). Claude names every price beforehand.</>,
                )}
              </div>
            </div>
          </div>
          <ul className="m-0 mt-3.5 grid list-none gap-3 p-0 md:grid-cols-3">
            {vorschlag.aufgaben.map(a => <AufgabeKarte key={a.id} a={a} />)}
          </ul>
        </li>
      </ol>
      {passend && (
        <p className="m-0 border-t border-linie-zart px-4 py-3 text-[0.8125rem] text-text-hell sm:px-5">
          {t('Die Reihenfolge passt zu deinen Antworten nach der Registrierung.', 'The order matches your answers after signing up.')}{' '}
          <Verweis to="/welcome?edit=1&next=%2Faccount">{t('Antworten ändern', 'Change answers')}</Verweis>
        </p>
      )}
    </Panel>
  );
}

/** Ein kurzer Wartezustand mit Satz. Fuer Seiten, deren Inhalt erst nach einer Pruefung feststeht. */
export function Laedt({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="m-0 flex items-center gap-2.5 text-[0.9375rem] text-text-leise">
      <Kreisel className="text-marke" />
      {children}
    </p>
  );
}
